import {
  type ChinaTransport,
  type ChinaTransportNode,
  type ChinaTransportPlace,
  type ChinaTransportRoute,
  type ChinaTransportRouteOption,
  greatCircleKm,
  type TransportZone,
} from "@-/dev.improve.haniverse.data.china-transport";
import memoize from "@-/com.npmjs.memoize";
import {
  COACH_MAX_KM,
  COACH_MIN_KM,
  COACH_OVERHEAD_MINUTES,
  COACH_SPEED_KMH,
  coachFareCny,
  HAINAN_FERRY_MINUTES,
  METRO_DETOUR_FACTOR,
  METRO_MIN_KM,
  METRO_OVERHEAD_MINUTES,
  METRO_SPEED_KMH,
  metroFareCny,
  ROAD_DETOUR_FACTOR,
  TRANSFER_MINUTES,
} from "#travel-rates";

export type TransportMode = "bus" | "flight" | "hsr" | "metro" | "rail";
export type SeatClass = "economy" | "hard-seat" | "hard-sleeper" | "second" | "standard";

export interface TravelLeg {
  distanceKm: number;
  durationMinutes: number;
  /** A representative service, e.g. `G1`, when timetabled. */
  example: string | null;
  fareCny: number;
  /** Hub key or place adcode the leg starts from. */
  from: number;
  mode: TransportMode;
  seatClass: SeatClass;
  /** `timetable`: a real schedule and fare; `estimate`: priced from published per-km rates. */
  source: "estimate" | "timetable";
  sourceUrl: string | null;
  to: number;
}

export interface TravelOption {
  /** Han to pay: the fare rounded up to whole yuan (1 Han = ¥1). */
  costHan: number;
  /** Great-circle distance between the two places' seats. */
  distanceKm: number;
  durationMinutes: number;
  fareCny: number;
  /** Stable across recomputation: the legs' modes, classes and endpoints. */
  id: string;
  legs: readonly TravelLeg[];
  /** The mode of the longest leg: how the journey is labelled. */
  mode: TransportMode;
  seatClass: SeatClass;
  source: "estimate" | "mixed" | "timetable";
}

interface TransportIndex {
  hubByProvince: ReadonlyMap<number, ChinaTransportNode>;
  nodeByKey: ReadonlyMap<number, ChinaTransportNode>;
  placeByAdcode: ReadonlyMap<number, ChinaTransportPlace>;
  routeByKey: ReadonlyMap<string, ChinaTransportRoute>;
}

const routeKey = (a: number, b: number) => `${Math.min(a, b)}-${Math.max(a, b)}`;

const indexTransport = memoize(
  (transport: ChinaTransport): TransportIndex => ({
    hubByProvince: new Map(
      transport.nodes
        .filter((node) => node.isProvinceHub)
        .map((node) => [node.provinceAdcode, node]),
    ),
    nodeByKey: new Map(transport.nodes.map((node) => [node.hubKey, node])),
    placeByAdcode: new Map(transport.places.map((place) => [place.adcode, place])),
    routeByKey: new Map(transport.routes.map((route) => [routeKey(route.from, route.to), route])),
  }),
  { cacheKey: ([transport]) => transport },
);

function timetableLeg(option: ChinaTransportRouteOption, from: number, to: number): TravelLeg {
  return {
    distanceKm: option.distanceKm,
    durationMinutes: option.durationMinutes,
    example: option.example,
    fareCny: option.fareCny,
    from,
    mode: option.mode,
    seatClass: option.seatClass,
    source: "timetable",
    sourceUrl: option.sourceUrl,
    to,
  };
}

function coachLeg(
  from: { key: number; lonLat: readonly [number, number]; zone: TransportZone },
  to: { key: number; lonLat: readonly [number, number]; zone: TransportZone },
): TravelLeg | null {
  const roadKm = Math.round(greatCircleKm(from.lonLat, to.lonLat) * ROAD_DETOUR_FACTOR);
  if (roadKm < COACH_MIN_KM || roadKm > COACH_MAX_KM) {
    return null;
  }
  const crossesStrait = (from.zone === "hainan") !== (to.zone === "hainan");
  return {
    distanceKm: roadKm,
    durationMinutes: Math.round(
      (roadKm / COACH_SPEED_KMH) * 60 +
        COACH_OVERHEAD_MINUTES +
        (crossesStrait ? HAINAN_FERRY_MINUTES : 0),
    ),
    example: null,
    fareCny: coachFareCny(roadKm),
    from: from.key,
    mode: "bus",
    seatClass: "standard",
    source: "estimate",
    sourceUrl: null,
    to: to.key,
  };
}

/** A local-transit hop between a district and its region's hub seat, or between two districts. */
function metroLeg(
  place: ChinaTransportPlace,
  from: { key: number; lonLat: readonly [number, number] },
  to: { key: number; lonLat: readonly [number, number] },
): TravelLeg | null {
  if (place.metro === null) {
    return null;
  }
  const km = Math.round(greatCircleKm(from.lonLat, to.lonLat) * METRO_DETOUR_FACTOR);
  if (km < METRO_MIN_KM) {
    return null;
  }
  return {
    distanceKm: km,
    durationMinutes: Math.round((km / METRO_SPEED_KMH) * 60 + METRO_OVERHEAD_MINUTES),
    example: null,
    fareCny: metroFareCny(place.metro, km),
    from: from.key,
    mode: "metro",
    seatClass: "standard",
    source: "estimate",
    sourceUrl: null,
    to: to.key,
  };
}

function optionsBetween(index: TransportIndex, a: ChinaTransportNode, b: ChinaTransportNode) {
  return index.routeByKey.get(routeKey(a.hubKey, b.hubKey))?.options ?? [];
}

/** The quickest way along a feeder leg (a city to its capital): ground first, then air, then coach. */
function feederLeg(
  index: TransportIndex,
  from: ChinaTransportNode,
  to: ChinaTransportNode,
): TravelLeg | null {
  const options = optionsBetween(index, from, to);
  const ground = options.filter(
    (option) => option.mode !== "flight" && option.seatClass !== "hard-sleeper",
  );
  const pool = ground.length > 0 ? ground : options;
  let quickest: ChinaTransportRouteOption | null = null;
  for (const option of pool) {
    if (quickest === null || option.durationMinutes < quickest.durationMinutes) {
      quickest = option;
    }
  }
  if (quickest !== null) {
    return timetableLeg(quickest, from.hubKey, to.hubKey);
  }
  return coachLeg(
    { key: from.hubKey, lonLat: from.lonLat, zone: from.zone },
    { key: to.hubKey, lonLat: to.lonLat, zone: to.zone },
  );
}

function composeOption(
  legs: readonly (TravelLeg | null)[],
  distanceKm: number,
): TravelOption | null {
  const present = legs.filter((leg) => leg !== null);
  let main: TravelLeg | null = null;
  let fareCny = 0;
  let durationMinutes = 0;
  for (const leg of present) {
    if (main === null || leg.distanceKm > main.distanceKm) {
      main = leg;
    }
    fareCny += leg.fareCny;
    durationMinutes += leg.durationMinutes;
  }
  if (main === null) {
    return null;
  }
  const sources = new Set(present.map((leg) => leg.source));
  return {
    costHan: Math.ceil(fareCny),
    distanceKm,
    durationMinutes: durationMinutes + TRANSFER_MINUTES * (present.length - 1),
    fareCny,
    id: present.map((leg) => `${leg.mode}:${leg.seatClass}:${leg.from}>${leg.to}`).join("|"),
    legs: present,
    mode: main.mode,
    seatClass: main.seatClass,
    source: sources.size > 1 ? "mixed" : main.source,
  };
}

/**
 * Every realistic way to travel between two map places, cheapest first.
 *
 * - Places sharing a hub (two Beijing districts) take local transit.
 * - Nodes with a real timetable between them get one option per timetabled mode and class.
 * - Otherwise the journey runs through the provincial capitals: city → capital → capital → city,
 *   each leg the real timetable (the feeder legs take the quickest ground option).
 * - A coach is always offered where the road allows, priced from published per-km rates.
 */
export function resolveTravelOptions(
  fromAdcode: number,
  toAdcode: number,
  transport: ChinaTransport,
): TravelOption[] {
  const index = indexTransport(transport);
  const from = index.placeByAdcode.get(fromAdcode);
  const to = index.placeByAdcode.get(toAdcode);
  if (from === undefined || to === undefined || fromAdcode === toAdcode) {
    return [];
  }
  const distanceKm = Math.round(greatCircleKm(from.lonLat, to.lonLat));
  if (from.hubKey === to.hubKey) {
    const option = composeOption(
      [
        metroLeg(
          from,
          { key: fromAdcode, lonLat: from.lonLat },
          { key: toAdcode, lonLat: to.lonLat },
        ),
      ],
      distanceKm,
    );
    return option === null ? [] : [option];
  }
  const fromNode = index.nodeByKey.get(from.hubKey);
  const toNode = index.nodeByKey.get(to.hubKey);
  if (fromNode === undefined || toNode === undefined) {
    return [];
  }
  // Districts of a shared hub reach its stations and airports by metro.
  const access = metroLeg(
    from,
    { key: fromAdcode, lonLat: from.lonLat },
    { key: fromNode.hubKey, lonLat: fromNode.lonLat },
  );
  const egress = metroLeg(
    to,
    { key: toNode.hubKey, lonLat: toNode.lonLat },
    { key: toAdcode, lonLat: to.lonLat },
  );

  const options: TravelOption[] = [];
  const add = (option: TravelOption | null) => {
    if (option !== null && !options.some((existing) => existing.id === option.id)) {
      options.push(option);
    }
  };

  const direct = optionsBetween(index, fromNode, toNode);
  for (const option of direct) {
    add(
      composeOption(
        [access, timetableLeg(option, fromNode.hubKey, toNode.hubKey), egress],
        distanceKm,
      ),
    );
  }
  if (direct.length === 0) {
    const fromHub = index.hubByProvince.get(fromNode.provinceAdcode);
    const toHub = index.hubByProvince.get(toNode.provinceAdcode);
    if (fromHub !== undefined && toHub !== undefined) {
      const feederOut =
        fromHub.hubKey === fromNode.hubKey ? null : feederLeg(index, fromNode, fromHub);
      const feederInReverse =
        toHub.hubKey === toNode.hubKey ? null : feederLeg(index, toNode, toHub);
      const feederIn =
        feederInReverse === null
          ? null
          : { ...feederInReverse, from: toHub.hubKey, to: toNode.hubKey };
      const feedersOk =
        (fromHub.hubKey === fromNode.hubKey || feederOut !== null) &&
        (toHub.hubKey === toNode.hubKey || feederIn !== null);
      if (feedersOk && fromHub.hubKey === toHub.hubKey) {
        add(composeOption([access, feederOut, feederIn, egress], distanceKm));
      } else if (feedersOk) {
        for (const trunk of optionsBetween(index, fromHub, toHub)) {
          add(
            composeOption(
              [
                access,
                feederOut,
                timetableLeg(trunk, fromHub.hubKey, toHub.hubKey),
                feederIn,
                egress,
              ],
              distanceKm,
            ),
          );
        }
      }
    }
  }
  add(
    composeOption(
      [
        coachLeg(
          { key: fromAdcode, lonLat: from.lonLat, zone: fromNode.zone },
          { key: toAdcode, lonLat: to.lonLat, zone: toNode.zone },
        ),
      ],
      distanceKm,
    ),
  );
  return options.sort((a, b) => a.fareCny - b.fareCny || a.durationMinutes - b.durationMinutes);
}
